/**
 * Cart Wall Editor
 * Édition de la hauteur/largeur du mur sur une ligne de papier peint AUTOCOLLANT
 * dans le tiroir panier. Recalcule la surface + la quantité facturée et met à
 * jour la ligne (quantité + propriétés) via /cart/change.js.
 *
 * - Pas de +/- sur ces lignes : la quantité (m²) découle de H × L.
 * - Chaque dimension est arrondie vers le haut au 0,5 cm le plus proche.
 * - Surface = H × L / 10000 ; quantité facturée = arrondi entier supérieur.
 */
class CartWallEditor extends HTMLElement {
  connectedCallback() {
    this.line = this.dataset.line;
    this.heightInput = this.querySelector('[data-ref="cart-wc-height"]');
    this.widthInput = this.querySelector('[data-ref="cart-wc-width"]');
    this.errorEl = this.querySelector('[data-ref="cart-wc-error"]');

    this._debounce = null;
    const onChange = () => this.scheduleUpdate();
    this.heightInput?.addEventListener('change', onChange);
    this.widthInput?.addEventListener('change', onChange);
  }

  fmt(n) {
    return n.toLocaleString('fr-FR', { maximumFractionDigits: 2 });
  }

  scheduleUpdate() {
    clearTimeout(this._debounce);
    this._debounce = setTimeout(() => this.applyUpdate(), 400);
  }

  applyUpdate() {
    const rawH = parseFloat((this.heightInput?.value || '').replace(',', '.'));
    const rawW = parseFloat((this.widthInput?.value || '').replace(',', '.'));

    if (!rawH || !rawW || rawH <= 0 || rawW <= 0) {
      this.showError('Renseignez une hauteur et une largeur valides.');
      return;
    }
    this.showError('');

    const hCm = rawH; // dimensions gardées telles que saisies par le client
    const wCm = rawW;
    const surface = (hCm * wCm) / 10000;
    const quantity = Math.ceil(surface);

    this.enableLoading();

    // Contexte : tiroir panier (re-render via sections) OU page /cart (reload)
    const cartDrawer = this.closest('cart-drawer');
    const payload = {
      line: this.line,
      quantity,
      properties: {
        'Hauteur du mur': `${this.fmt(hCm)} cm`,
        'Largeur du mur': `${this.fmt(wCm)} cm`,
        'Surface du mur': `${this.fmt(surface)} m²`,
      },
    };
    if (cartDrawer) {
      payload.sections = cartDrawer.dataset.sectionId;
      payload.sections_url = window.location.pathname;
    }

    fetch(Theme.routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.errors) {
          this.showError("La mise à jour a échoué.");
          this.disableLoading();
          return;
        }
        if (cartDrawer) {
          // Tiroir : re-render via l'event cart:updated (pas de reload)
          document.dispatchEvent(
            new CustomEvent('cart:updated', {
              bubbles: true,
              detail: {
                resource: {},
                data: {
                  itemCount: data.item_count,
                  source: 'cart-wall-editor',
                  sections: data.sections,
                },
              },
            })
          );
        } else {
          // Page panier : on recharge (comme le composant cart-item du thème)
          window.location.reload();
        }
      })
      .catch((e) => {
        console.error(e);
        this.showError("La mise à jour a échoué.");
        this.disableLoading();
      });
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message || '';
    this.errorEl.hidden = !message;
  }

  enableLoading() {
    this.closest('cart-drawer-item')?.classList.add('cart-drawer__item--loading');
  }

  disableLoading() {
    this.closest('cart-drawer-item')?.classList.remove('cart-drawer__item--loading');
  }
}

if (!customElements.get('cart-wall-editor')) {
  customElements.define('cart-wall-editor', CartWallEditor);
}
